<template>
  <div class="d-flex" style="min-height: 45vw">
    <div class="content p-4 w-100">
      <div class="d-flex">
        <div class="col-5">
          <h4 class="card-title mt-0">{{ title }}</h4>
        </div>
      </div>
      <div class="w-100 mb-2 mt-1 position-relative">
        <div
          class="d-flex"
          :class="buttonsStyle"
        >
          <v-btn
            v-if="isDisplayCvrList"
            class="m-1"
            @click="changeDisplayCvrList(false)"
          >
            {{ i18n.global.t('pages.common.button.return') }}
          </v-btn>
          <div>
            <v-btn
              @click="reload"
              class="m-1"
            >
              {{ i18n.global.t('pages.common.button.displayReload') }}
            </v-btn>
            <UploadPanel
              v-if="createAuthority"
              :reload="reload"
              :showFlash="showFlash"
            />
          </div>
        </div>
      </div>
      <div :class="listsClass">
        <VehiclesList
          ref="VehiclesListComponent"
          :reload="reload"
          :showFlash="showFlash"
          :openCvrList="changeDisplayCvrList"
          :isDisplayCvrList="isDisplayCvrList"
          :selectedVehicle="selectedVehicle"
          :createAuthority="createAuthority"
        />
        <div
          v-if="isDisplayCvrList"
          :class="cvrListClass"
        >
          <List
            class="mb-2"
            v-model="cvrUploadsFilterParams"
            :items="items"
            :accounts="accounts"
            :showFlash="showFlash"
            :reload="reload"
            :listLoading="listLoading"
            :updateAuthority="updateAuthority"
            @search="getCvrUploads"
            :isManagementOffice="isManagementOffice"
          />
          <PaginateWithCount
            class="justify-end"
            :totalPage="totalPage"
            v-model="currentPage"
            :itemsPerPage="itemsPerPage"
            :allElementsCount="allElementsCount"
            :showItemCount="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { DEFAULT_SELECTED_STATUSES } from "@/services/consts/cvr_upload.ts";
import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
import UploadPanel from "@/components/CvrConvert/UploadPanel";
import List from "@/components/CvrConvert/CvrUploadList";
import PaginateWithCount from "@/components/PaginateWithCount.vue";
import Api from "@/services/api/ApiServiceFabrick";
import { ref, computed, onMounted, watch } from "vue";
import VehiclesList from "@/components/CvrConvert/VehiclesListParts/VehiclesList.vue"
import i18n from '@/lang/i18n'
import { MANAGEMENT_OFFICE_TYPE } from "@/services/consts";
import { saveDataToLocalStorage, loadDataFromLocalStorage } from "@/services/functions/localStorage.ts";

const props = defineProps(["showFlash"]);
const showFlash = ref(props.showFlash);
const title = i18n.global.t('pages.cvr_uploads.title')
document.title = title;

const currentPage = ref(1);
const totalPage = ref(0);
const itemsPerPage = ref(50);
const allElementsCount = ref(0);
const items = ref([]);
const PARENT_ONLY = 1;
const listLoading = ref(false);
const accounts = ref([]);
const isDisplayCvrList = ref(false);
const selectedVehicle = ref({});
const VehiclesListComponent = ref(null);
const createAuthority = ref(true);
const updateAuthority = ref(true);
const cvrUploadsFilterParams = ref({
  status: DEFAULT_SELECTED_STATUSES
})
const isManagementOffice = ref(false);

onMounted(() => {
  getAuthority()
  getAccounts()
  Api()
  .getSelfAccount()
  .then((data) => {
    isManagementOffice.value = data?.login_organization_type == MANAGEMENT_OFFICE_TYPE;
  })

  const savedStatuses = loadDataFromLocalStorage('cvrUploadsFilterParams');
  if (savedStatuses && savedStatuses.status) {
    cvrUploadsFilterParams.value.status = savedStatuses.status;
  }
})

const getAuthority = async() => {
  const authorityList = await AuthorityCacheApi.getWithParse()
  createAuthority.value = !!authorityList["octlink.cvr_upload.create"];
  updateAuthority.value = !!authorityList["octlink.cvr_upload.update"];
}

const getAccounts = () => {
  Api().autoCompleteAccounts().then((data) => {
    accounts.value = data
  })
}

const reload = () => {
  currentPage.value = 1;
  VehiclesListComponent.value.getCvrUploadSummaries()
  if (isDisplayCvrList.value) getCvrUploads()
};

const getCvrUploads = () => {
  listLoading.value = true
  const params = makeQueryParams()
  saveDataToLocalStorage('cvrUploadsFilterParams', cvrUploadsFilterParams.value);
  Api().getCvrUploads(params).then((data) => {
    listLoading.value = false
    items.value = data.items
    currentPage.value = data.current_page;
    totalPage.value = data.total_page;
    allElementsCount.value = data.total;
    itemsPerPage.value = data.per_page;
  }).catch((err) => {
    listLoading.value = false
    switch (err.response.status) {
      case 403:
        location.reload();
        break;
    }
  });
}

function makeQueryParams() {
  return {
    page: currentPage.value,
    parentOnly: PARENT_ONLY,
    deviceId: selectedVehicle.value.device_id,
    statuses: cvrUploadsFilterParams.value.status.join(',')
  }
}

const buttonsStyle = computed(() => {
  return isDisplayCvrList.value ? 'justify-space-between' : 'justify-end'
})

const listsClass = computed(() => {
  return isDisplayCvrList.value ? 'd-flex w-100' : ''
})

const cvrListClass = computed(() => {
  return isDisplayCvrList.value ? 'cvr-list display' : 'cvr-list'
})

const changeDisplayCvrList = (bool, vehicle = null) => {
  isDisplayCvrList.value = bool
  if (listLoading.value) return
  if (isDisplayCvrList.value) {
    items.value = []
    currentPage.value = 1;
    selectedVehicle.value = vehicle
    getCvrUploads()
  }
}

watch(currentPage, () => {
  getCvrUploads()
});

</script>

<style scoped>
.cvr-list {
  z-index: 999;
  right: -120%;
	width: 65%;
	transition: 0.5s;
}
.cvr-list.display {
  right: 0;
  width: 90%;
  margin-left: 25px;
}
</style>
